import type { PersistedDraft } from '../../application/models';
import type { DraftStore } from '../../application/ports';
import type { TestamentId } from '../../domain';

/** The one call this needs from `navigator.storage`; absent where the browser offers none. */
export interface PersistentStorage {
  persist(): Promise<boolean>;
}

/**
 * A draft store that asks the browser, once, to keep what it holds. Without that, a browser short
 * of space — or Safari, after a week of not opening the app — may clear a site's data, and the
 * will with it. The request is made at the first write, never waited on, and a refusal changes
 * nothing: the draft is written all the same.
 */
export class PersistentDraftStore implements DraftStore {
  private asked = false;

  constructor(
    private readonly inner: DraftStore,
    private readonly storage: PersistentStorage | undefined,
  ) {}

  load(id: TestamentId): Promise<PersistedDraft | null> {
    return this.inner.load(id);
  }

  save(draft: PersistedDraft): Promise<void> {
    this.askOnce();
    return this.inner.save(draft);
  }

  private askOnce(): void {
    if (this.asked) return;
    this.asked = true;
    try {
      void this.storage?.persist().catch(() => false);
    } catch {
      // A browser that throws instead of answering has answered no.
    }
  }
}
